// Servicio principal de exportación modular
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getExcelTemplate, excelConfig } from '../templates/export/excelConfig';
import { getPdfTemplate, pdfConfig } from '../templates/export/pdfConfig';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatDate,
  numberToText,
  formatUnit,
  calculateTotals,
  groupByPartida,
  validateExportData,
  prepareExportData,
  generateFileName
} from '../templates/export/formatters';

class ExportService {
  constructor() {
    this.excelTemplate = 'standard';
    this.pdfTemplate = 'standard';
    this.customSettings = {};
  }

  // Configurar plantilla a usar
  setTemplate(type, templateName) {
    if (type === 'excel') {
      this.excelTemplate = templateName;
    } else if (type === 'pdf') {
      this.pdfTemplate = templateName;
    }
  }

  // Aplicar configuraciones personalizadas
  setCustomSettings(settings) {
    this.customSettings = settings;
  }

  // Exportar a Excel
  exportToExcel(presupuesto, conceptos, porcentajes, templateName = null) {
    try {
      // Obtener plantilla
      const template = getExcelTemplate(templateName || this.excelTemplate);

      // Validar datos
      const validation = validateExportData({ presupuesto, conceptos });
      if (!validation.isValid) {
        throw new Error(`Datos inválidos: ${validation.errors.join(', ')}`);
      }

      // Preparar datos
      const exportData = prepareExportData(presupuesto, conceptos, porcentajes);

      // Crear libro de Excel
      const wb = XLSX.utils.book_new();

      // Construir datos de la hoja según la plantilla
      const wsData = this.buildExcelData(exportData, template);

      // Crear hoja
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Aplicar estilos (si la biblioteca lo permite)
      this.applyExcelStyles(ws, template);

      // Agregar hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, template.hoja?.nombre || 'Presupuesto');

      // Generar nombre de archivo
      const fileName = generateFileName(
        excelConfig.archivo.nombreBase,
        {
          incluirFecha: excelConfig.archivo.incluirFecha,
          incluirFolio: excelConfig.archivo.incluirFolio,
          folio: presupuesto.codigo,
          extension: '.xlsx'
        }
      );

      // Descargar archivo
      XLSX.writeFile(wb, fileName);

      return { success: true, fileName };
    } catch (error) {
      console.error('Error exportando a Excel:', error);
      return { success: false, error: error.message };
    }
  }

  // Construir datos para Excel según plantilla
  buildExcelData(exportData, template) {
    const { presupuesto, conceptos, totales, porcentajes } = exportData;
    const wsData = [];

    // Obtener configuraciones de localStorage
    const empresaNombre = localStorage.getItem('empresa_nombre') || template.secciones?.encabezado?.contenido?.empresa || 'Mi Empresa S.A. de C.V.';
    const telefono = localStorage.getItem('empresa_telefono') || '';
    const email = localStorage.getItem('empresa_email') || '';
    const web = localStorage.getItem('empresa_web') || '';
    const direccion = localStorage.getItem('empresa_direccion') || '';
    const mostrarTelefono = localStorage.getItem('mostrar_telefono') !== 'false';
    const mostrarEmail = localStorage.getItem('mostrar_email') !== 'false';
    const mostrarWeb = localStorage.getItem('mostrar_web') !== 'false';
    const mostrarDireccion = localStorage.getItem('mostrar_direccion') !== 'false';
    const incluirFecha = localStorage.getItem('incluir_fecha') !== 'false';
    const incluirFolio = localStorage.getItem('incluir_folio') !== 'false';

    // Título
    if (template.secciones?.encabezado?.mostrar) {
      wsData.push([empresaNombre]);

      // Información de contacto
      const contactInfo = [];
      if (mostrarTelefono && telefono) contactInfo.push(`Tel: ${telefono}`);
      if (mostrarEmail && email) contactInfo.push(`Email: ${email}`);
      if (mostrarWeb && web) contactInfo.push(`Web: ${web}`);
      if (contactInfo.length > 0) {
        wsData.push([contactInfo.join(' | ')]);
      }
      if (mostrarDireccion && direccion) {
        wsData.push([direccion]);
      }

      wsData.push([template.secciones.encabezado.contenido?.titulo || 'PRESUPUESTO DE OBRA']);
      wsData.push([]);

      // Información del encabezado
      template.secciones.encabezado.campos.forEach(campo => {
        if (campo.mostrar !== false) {
          let valor = presupuesto[campo.campo];
          if (campo.formato === 'fecha') {
            valor = formatDate(valor);
          }
          wsData.push([campo.etiqueta, valor]);
        }
      });
      wsData.push([]);
    }

    // Encabezados de tabla
    const headers = [];
    const columnas = template.columnas?.visibles || {};

    // Obtener configuración de columnas de localStorage
    const colCodigo = localStorage.getItem('col_codigo') !== 'false';
    const colUnidad = localStorage.getItem('col_unidad') !== 'false';
    const colCantidad = localStorage.getItem('col_cantidad') !== 'false';
    const colPU = localStorage.getItem('col_pu') !== 'false';
    const colImporte = localStorage.getItem('col_importe') !== 'false';

    if (columnas.partida) headers.push('Partida');
    if (colCodigo && columnas.codigo !== false) headers.push('Código');
    if (columnas.descripcion !== false) headers.push('Descripción');
    if (colUnidad && columnas.unidad !== false) headers.push('Unidad');
    if (colCantidad && columnas.cantidad !== false) headers.push('Cantidad');
    if (colPU && columnas.precioUnitario !== false) headers.push('P.U.');
    if (colImporte && columnas.importe !== false) headers.push('Importe');
    if (columnas.desglose) {
      headers.push('Materiales', 'M.O.', 'Maquinaria');
    }

    wsData.push(headers);

    // Conceptos
    conceptos.forEach(c => {
      const row = [];
      if (columnas.partida) row.push(c.partida || '');
      if (colCodigo && columnas.codigo !== false) row.push(c.codigo || '');
      if (columnas.descripcion !== false) row.push(c.descripcion);
      if (colUnidad && columnas.unidad !== false) row.push(c.unidad);
      if (colCantidad && columnas.cantidad !== false) row.push(c.cantidad);
      if (colPU && columnas.precioUnitario !== false) row.push(c.precio_unitario);
      if (colImporte && columnas.importe !== false) row.push(c.importe);
      if (columnas.desglose && c.analisis) {
        row.push(
          c.analisis.materiales || 0,
          c.analisis.manoObra || 0,
          c.analisis.maquinaria || 0
        );
      }
      wsData.push(row);
    });

    // Totales
    if (template.secciones?.totales?.mostrar) {
      wsData.push([]);

      // Obtener configuración de totales de localStorage
      const mostrarIndirectos = localStorage.getItem('mostrar_indirectos') !== 'false';
      const mostrarFinanciamiento = localStorage.getItem('mostrar_financiamiento') !== 'false';
      const mostrarUtilidad = localStorage.getItem('mostrar_utilidad') !== 'false';
      const mostrarIVA = localStorage.getItem('mostrar_iva') !== 'false';

      template.secciones.totales.campos.forEach(campo => {
        let mostrarCampo = campo.mostrar;

        // Aplicar configuración de localStorage
        if (campo.campo === 'indirectos' && !mostrarIndirectos) mostrarCampo = false;
        if (campo.campo === 'financiamiento' && !mostrarFinanciamiento) mostrarCampo = false;
        if (campo.campo === 'utilidad' && !mostrarUtilidad) mostrarCampo = false;
        if (campo.campo === 'iva' && !mostrarIVA) mostrarCampo = false;

        if (mostrarCampo) {
          const row = new Array(headers.length - 2).fill('');
          let etiqueta = campo.etiqueta;

          if (campo.mostrarPorcentaje && porcentajes[campo.campo.replace('total', '').toLowerCase()]) {
            const porcentaje = porcentajes[campo.campo.replace('total', '').toLowerCase()];
            etiqueta += ` (${porcentaje}%)`;
          }

          row.push(etiqueta);
          row.push(totales[campo.campo]);
          wsData.push(row);
        }
      });
    }

    // Pie de página
    if (template.secciones?.piePagina?.mostrar) {
      wsData.push([]);
      wsData.push([template.secciones.piePagina.texto || '']);
    }

    return wsData;
  }

  // Aplicar estilos a Excel
  applyExcelStyles(ws, template) {
    // Configurar anchos de columna
    if (template.columnas?.anchos) {
      const cols = [];
      Object.values(template.columnas.anchos).forEach(ancho => {
        cols.push({ wch: ancho });
      });
      ws['!cols'] = cols;
    }

    // Nota: Para aplicar estilos completos se necesita una biblioteca adicional
    // como xlsx-style o sheetjs-style
    return ws;
  }

  // Exportar a PDF
  exportToPDF(presupuesto, conceptos, porcentajes, templateName = null) {
    try {
      // Obtener plantilla
      const template = getPdfTemplate(templateName || this.pdfTemplate);

      // Validar datos
      const validation = validateExportData({ presupuesto, conceptos });
      if (!validation.isValid) {
        throw new Error(`Datos inválidos: ${validation.errors.join(', ')}`);
      }

      // Preparar datos
      const exportData = prepareExportData(presupuesto, conceptos, porcentajes);

      // Crear documento PDF
      const doc = new jsPDF({
        orientation: template.pagina.orientacion,
        unit: 'mm',
        format: template.pagina.formato
      });

      // Establecer metadatos
      doc.setProperties({
        title: pdfConfig.metadatos.titulo,
        subject: pdfConfig.metadatos.asunto,
        author: pdfConfig.metadatos.autor,
        keywords: pdfConfig.metadatos.palabrasClave,
        creator: pdfConfig.metadatos.creador
      });

      // Renderizar contenido según plantilla
      this.renderPdfContent(doc, exportData, template);

      // Generar nombre de archivo
      const fileName = generateFileName(
        pdfConfig.archivo.nombreBase,
        {
          incluirFecha: pdfConfig.archivo.incluirFecha,
          incluirFolio: pdfConfig.archivo.incluirFolio,
          folio: presupuesto.codigo,
          extension: '.pdf'
        }
      );

      // Guardar PDF
      doc.save(fileName);

      return { success: true, fileName };
    } catch (error) {
      console.error('Error exportando a PDF:', error);
      return { success: false, error: error.message };
    }
  }

  // Renderizar contenido del PDF
  renderPdfContent(doc, exportData, template) {
    const { presupuesto, conceptos, totales, porcentajes, totalEnTexto } = exportData;

    // Obtener márgenes de localStorage
    const marginTop = parseInt(localStorage.getItem('margen_superior')) || template.pagina.margenes.top;
    const marginBottom = parseInt(localStorage.getItem('margen_inferior')) || template.pagina.margenes.bottom;
    const marginLeft = parseInt(localStorage.getItem('margen_izquierdo')) || template.pagina.margenes.left;
    const marginRight = parseInt(localStorage.getItem('margen_derecho')) || template.pagina.margenes.right;

    // Aplicar márgenes al template
    template.pagina.margenes = {
      top: marginTop,
      bottom: marginBottom,
      left: marginLeft,
      right: marginRight
    };

    let currentY = marginTop;

    // Renderizar encabezado
    if (template.encabezado?.mostrar) {
      currentY = this.renderPdfHeader(doc, presupuesto, template, currentY);
    }

    // Renderizar tabla de conceptos
    if (conceptos.length > 0) {
      currentY = this.renderPdfTable(doc, conceptos, template, currentY);
    }

    // Renderizar totales
    if (template.totales?.mostrar) {
      currentY = this.renderPdfTotals(doc, totales, porcentajes, totalEnTexto, template, currentY);
    }

    // Renderizar pie de página
    if (template.piePagina?.mostrar) {
      this.renderPdfFooter(doc, template);
    }

    // Agregar numeración de páginas
    if (template.pagina.numeracion) {
      this.addPageNumbers(doc, template);
    }
  }

  // Renderizar encabezado del PDF
  renderPdfHeader(doc, presupuesto, template, startY) {
    let y = startY;
    const { encabezado } = template;
    const { estilos } = template;

    // Obtener configuraciones de localStorage
    const empresaNombre = localStorage.getItem('empresa_nombre') || 'Mi Empresa S.A. de C.V.';
    const telefono = localStorage.getItem('empresa_telefono') || '';
    const email = localStorage.getItem('empresa_email') || '';
    const web = localStorage.getItem('empresa_web') || '';
    const direccion = localStorage.getItem('empresa_direccion') || '';
    const mostrarTelefono = localStorage.getItem('mostrar_telefono') !== 'false';
    const mostrarEmail = localStorage.getItem('mostrar_email') !== 'false';
    const mostrarWeb = localStorage.getItem('mostrar_web') !== 'false';
    const mostrarDireccion = localStorage.getItem('mostrar_direccion') !== 'false';
    const incluirFecha = localStorage.getItem('incluir_fecha') !== 'false';
    const incluirFolio = localStorage.getItem('incluir_folio') !== 'false';
    const colorPrincipal = localStorage.getItem('color_principal') || '#1976d2';

    // Nombre de la empresa
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    const pageWidth = doc.internal.pageSize.width;
    doc.text(empresaNombre, pageWidth / 2, y, { align: 'center' });
    y += 8;

    // Información de contacto
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    const contactInfo = [];
    if (mostrarTelefono && telefono) contactInfo.push(`Tel: ${telefono}`);
    if (mostrarEmail && email) contactInfo.push(`Email: ${email}`);
    if (mostrarWeb && web) contactInfo.push(`Web: ${web}`);

    if (contactInfo.length > 0) {
      doc.text(contactInfo.join(' | '), pageWidth / 2, y, { align: 'center' });
      y += 5;
    }

    if (mostrarDireccion && direccion) {
      doc.text(direccion, pageWidth / 2, y, { align: 'center' });
      y += 5;
    }

    y += 3;

    // Título
    if (encabezado.contenido?.titulo) {
      doc.setFontSize(estilos.titulo?.fontSize || 16);
      doc.setFont(undefined, estilos.titulo?.fontStyle || 'bold');
      const pageWidth = doc.internal.pageSize.width;
      doc.text(encabezado.contenido.titulo, pageWidth / 2, y, { align: 'center' });
      y += estilos.titulo?.marginBottom || 10;
    }

    // Subtítulo
    if (encabezado.contenido?.subtitulo) {
      doc.setFontSize(estilos.subtitulo?.fontSize || 12);
      doc.setFont(undefined, 'normal');
      const pageWidth = doc.internal.pageSize.width;
      doc.text(encabezado.contenido.subtitulo, pageWidth / 2, y, { align: 'center' });
      y += estilos.subtitulo?.marginBottom || 5;
    }

    // Campos del encabezado
    doc.setFontSize(encabezado.estilo?.fontSize || 10);
    doc.setFont(undefined, 'normal');

    encabezado.campos?.forEach(campo => {
      if (campo.mostrar !== false && presupuesto[campo.campo]) {
        let valor = presupuesto[campo.campo];
        if (campo.formato === 'fecha') {
          valor = formatDate(valor);
        } else if (campo.formato === 'fechaCompleta') {
          valor = formatDate(valor, 'fechaCompleta');
        }

        const texto = `${campo.etiqueta} ${valor}`;
        doc.text(texto, template.pagina.margenes.left, y);
        y += encabezado.estilo?.lineHeight * (encabezado.estilo?.fontSize || 10) || 7;
      }
    });

    return y + 5;
  }

  // Renderizar tabla del PDF
  renderPdfTable(doc, conceptos, template, startY) {
    const { tabla } = template;

    // Obtener configuración de columnas de localStorage
    const colCodigo = localStorage.getItem('col_codigo') !== 'false';
    const colUnidad = localStorage.getItem('col_unidad') !== 'false';
    const colCantidad = localStorage.getItem('col_cantidad') !== 'false';
    const colPU = localStorage.getItem('col_pu') !== 'false';
    const colImporte = localStorage.getItem('col_importe') !== 'false';

    // Preparar encabezados basados en configuración
    const headers = [];
    const columnFields = [];

    tabla.columnas.forEach(col => {
      let incluir = true;
      if (col.campo === 'codigo' && !colCodigo) incluir = false;
      if (col.campo === 'unidad' && !colUnidad) incluir = false;
      if (col.campo === 'cantidad' && !colCantidad) incluir = false;
      if (col.campo === 'precio_unitario' && !colPU) incluir = false;
      if (col.campo === 'importe' && !colImporte) incluir = false;

      if (incluir && col.titulo) {
        headers.push(col.titulo);
        columnFields.push(col);
      }
    });

    // Preparar datos
    const tableData = conceptos.map(c => {
      return columnFields.map(col => {
        let valor = c[col.campo];

        if (col.formato === 'moneda') {
          valor = formatCurrency(valor);
        } else if (col.formato === 'numero') {
          valor = formatNumber(valor);
        } else if (col.formato === 'porcentaje') {
          valor = formatPercentage(valor);
        }

        return valor || '';
      });
    });

    // Configurar estilos de tabla
    const theme = tabla.tema || 'grid';
    const startYPos = startY || tabla.startY || 75;

    // Generar tabla
    doc.autoTable({
      head: [headers],
      body: tableData,
      startY: startYPos,
      theme: theme,
      styles: {
        fontSize: tabla.estilos?.fontSize || 10,
        cellPadding: tabla.estilos?.cellPadding || 2
      },
      headStyles: {
        fillColor: template.estilos?.encabezadoTabla?.fillColor || [68, 114, 196],
        textColor: template.estilos?.encabezadoTabla?.textColor || [255, 255, 255],
        fontSize: template.estilos?.encabezadoTabla?.fontSize || 11,
        fontStyle: template.estilos?.encabezadoTabla?.fontStyle || 'bold',
        halign: template.estilos?.encabezadoTabla?.alignment || 'center'
      },
      columnStyles: this.getColumnStyles(columnFields)
    });

    return doc.lastAutoTable.finalY + 10;
  }

  // Obtener estilos de columnas
  getColumnStyles(columnas) {
    const styles = {};

    columnas.forEach((col, index) => {
      styles[index] = {
        halign: col.alinear || 'left'
      };
      if (col.ancho && col.ancho !== 'auto') {
        styles[index].cellWidth = col.ancho;
      }
    });

    return styles;
  }

  // Renderizar totales del PDF
  renderPdfTotals(doc, totales, porcentajes, totalEnTexto, template, startY) {
    const { totales: totalesConfig } = template;
    let y = startY + (totalesConfig.espacioAntes || 10);

    // Obtener configuración de totales de localStorage
    const mostrarIndirectos = localStorage.getItem('mostrar_indirectos') !== 'false';
    const mostrarFinanciamiento = localStorage.getItem('mostrar_financiamiento') !== 'false';
    const mostrarUtilidad = localStorage.getItem('mostrar_utilidad') !== 'false';
    const mostrarIVA = localStorage.getItem('mostrar_iva') !== 'false';
    const pageWidth = doc.internal.pageSize.width;
    const marginRight = template.pagina.margenes.right;
    const labelWidth = totalesConfig.anchoEtiqueta || 60;

    let xPos;
    if (totalesConfig.alineacion === 'izquierda') {
      xPos = template.pagina.margenes.left;
    } else if (totalesConfig.alineacion === 'centro') {
      xPos = pageWidth / 2 - labelWidth / 2;
    } else {
      xPos = pageWidth - marginRight - labelWidth - 40;
    }

    doc.setFontSize(10);

    totalesConfig.campos?.forEach(campo => {
      let mostrarCampo = campo.mostrar;

      // Aplicar configuración de localStorage
      if (campo.campo === 'indirectos' && !mostrarIndirectos) mostrarCampo = false;
      if (campo.campo === 'financiamiento' && !mostrarFinanciamiento) mostrarCampo = false;
      if (campo.campo === 'utilidad' && !mostrarUtilidad) mostrarCampo = false;
      if (campo.campo === 'iva' && !mostrarIVA) mostrarCampo = false;

      if (mostrarCampo) {
        let etiqueta = campo.etiqueta;
        let valor = totales[campo.campo];

        // Agregar porcentaje si corresponde
        if (campo.mostrarPorcentaje) {
          const porcentajeKey = campo.campo === 'iva' ? 'iva' :
                                campo.campo.replace('total', '').toLowerCase();
          const porcentaje = porcentajes[porcentajeKey];
          if (porcentaje) {
            etiqueta += ` (${porcentaje}%)`;
          }
        }

        // Formatear valor
        if (campo.formato === 'moneda') {
          valor = formatCurrency(valor);
        }

        // Aplicar estilo especial si es resaltado
        if (campo.resaltar) {
          doc.setFont(undefined, 'bold');
          doc.setFontSize(12);
        } else {
          doc.setFont(undefined, 'normal');
          doc.setFontSize(10);
        }

        // Dibujar línea separadora si corresponde
        if (campo.separador) {
          doc.line(xPos, y - 2, xPos + labelWidth + 40, y - 2);
        }

        // Dibujar etiqueta y valor
        doc.text(etiqueta, xPos, y);
        doc.text(valor.toString(), xPos + labelWidth + 35, y, { align: 'right' });

        y += campo.resaltar ? 8 : 6;
      }
    });

    // Agregar total en texto si está configurado
    if (totalesConfig.incluirTextoTotal) {
      y += 5;
      doc.setFontSize(9);
      doc.setFont(undefined, 'italic');
      doc.text(`(${totalEnTexto})`, xPos, y);
      y += 8;
    }

    return y;
  }

  // Renderizar pie de página del PDF
  renderPdfFooter(doc, template) {
    const { piePagina } = template;
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    let y = pageHeight - template.pagina.margenes.bottom;

    // Agregar firmas si corresponde
    if (piePagina.contenido?.firmas && piePagina.contenido.espacioFirmas) {
      y = y - 40;
      const numFirmas = piePagina.contenido.espacioFirmas.length;
      const firmaWidth = (pageWidth - template.pagina.margenes.left - template.pagina.margenes.right) / numFirmas;

      piePagina.contenido.espacioFirmas.forEach((firma, index) => {
        const xPos = template.pagina.margenes.left + (firmaWidth * index) + (firmaWidth / 2);

        // Línea de firma
        doc.line(xPos - 30, y, xPos + 30, y);

        // Título de firma
        doc.setFontSize(9);
        doc.text(firma.titulo, xPos, y + 5, { align: 'center' });
      });
      y += 15;
    }

    // Agregar notas
    if (piePagina.contenido?.notas && piePagina.contenido.notas.length > 0) {
      doc.setFontSize(8);
      doc.setFont(undefined, 'italic');
      piePagina.contenido.notas.forEach(nota => {
        doc.text(nota, pageWidth / 2, y, { align: 'center' });
        y += 4;
      });
    }

    // Agregar texto del pie
    if (piePagina.contenido?.texto) {
      doc.setFontSize(template.estilos?.piePagina?.fontSize || 9);
      doc.setFont(undefined, template.estilos?.piePagina?.fontStyle || 'italic');
      doc.text(piePagina.contenido.texto, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }

    // Agregar información de contacto
    if (piePagina.contenido?.contacto?.mostrar) {
      const contacto = piePagina.contenido.contacto;
      doc.setFontSize(8);
      const contactText = [
        contacto.telefono,
        contacto.email,
        contacto.web
      ].filter(Boolean).join(' | ');

      doc.text(contactText, pageWidth / 2, pageHeight - 5, { align: 'center' });
    }
  }

  // Agregar números de página
  addPageNumbers(doc, template) {
    const pageCount = doc.internal.getNumberOfPages();
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');

      let pageText = template.pagina.numeracionFormato || 'Página {page} de {pages}';
      pageText = pageText.replace('{page}', i.toString());
      pageText = pageText.replace('{pages}', pageCount.toString());

      doc.text(pageText, pageWidth - template.pagina.margenes.right, pageHeight - 5, { align: 'right' });
    }
  }

  // Exportar con plantilla personalizada temporal
  exportWithCustomTemplate(type, data, customTemplate) {
    const { presupuesto, conceptos, porcentajes } = data;

    if (type === 'excel') {
      // Guardar plantilla actual
      const currentTemplate = this.excelTemplate;

      // Aplicar plantilla personalizada temporalmente
      this.excelTemplate = 'custom';

      // Exportar
      const result = this.exportToExcel(presupuesto, conceptos, porcentajes, customTemplate);

      // Restaurar plantilla original
      this.excelTemplate = currentTemplate;

      return result;
    } else if (type === 'pdf') {
      // Guardar plantilla actual
      const currentTemplate = this.pdfTemplate;

      // Aplicar plantilla personalizada temporalmente
      this.pdfTemplate = 'custom';

      // Exportar
      const result = this.exportToPDF(presupuesto, conceptos, porcentajes, customTemplate);

      // Restaurar plantilla original
      this.pdfTemplate = currentTemplate;

      return result;
    }
  }
}

// Crear instancia singleton
const exportService = new ExportService();

export default exportService;